import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import { useLocale } from '@/i18n/locale-provider';
import { usePolling } from '@/lib/use-async';
import { Badge, Card, ErrorPanel, Kpi, PageHeader, Spinner, Table, Td } from '@/components/ui';

interface Summary {
  range: { from: string; to: string };
  sales: { totalSales: number; totalTax: number; totalDiscount: number; averageOrderValue: number; invoiceCount: number };
  channels: { online: number; branch: number; mobile: number; agentSimulation: number };
  orders: { totalOrders: number; cancelledOrders: number; failedOrders: number };
  payments: { attempted: number; succeeded: number; failed: number; successRate: number };
  returns: { count: number; refundedAmount: number; returnRate: number };
  customers: { active: number; new: number; purchasing: number };
}

interface SalesSeries {
  series: Array<{ date: string; total: number; count: number }>;
}

interface BranchRow {
  branches: Array<{ branchId: string; code: string; name: string; nameAr: string | null; city: string; cityAr: string | null; invoiceCount: number; totalSales: number }>;
}

interface ProductRow {
  products: Array<{ productId: string; sku: string; productName: string; productNameAr: string | null; unitsSold: number; revenue: number }>;
}

interface InventoryHealth {
  totals: { trackedLines: number; unitsAvailable: number; unitsReserved: number };
  lowStock: Array<{
    productId: string; sku: string; name: string; nameAr: string | null;
    warehouseName: string; warehouseNameAr: string | null;
    quantityAvailable: number; reorderLevel: number;
  }>;
}

interface AgentStats {
  runs: { total: number; completed: number; failed: number; running: number };
  throughput: { runsInLastHour: number; ordersInLastHour: number };
  lastRun: { runNumber: string; status: string; finishedAt: string } | null;
}

interface AiUsage {
  answers: number;
  averageLatencyMs: number;
  verifiedShare: number;
  modelConfigured: boolean;
  model: string;
}

// The brand's channel colours, so every chart in the platform agrees.
const CHANNEL_COLOURS = ['#355872', '#91D5E5', '#9EBBD1', '#227589'];

export function DashboardPage() {
  const { t, n, money, date, fill, field } = useLocale();

  // Refreshed every 20s so the dashboard tracks the agent while it runs.
  const summary = usePolling(() => unwrap<Summary>(api.simulator.get('/analytics/summary')), 20000);
  const series = usePolling(() => unwrap<SalesSeries>(api.simulator.get('/analytics/sales')), 60000);
  const branches = usePolling(() => unwrap<BranchRow>(api.simulator.get('/analytics/branches')), 60000);
  const products = usePolling(() => unwrap<ProductRow>(api.simulator.get('/analytics/products?limit=8')), 60000);
  const inventory = usePolling(() => unwrap<InventoryHealth>(api.simulator.get('/analytics/inventory')), 60000);
  const agent = usePolling(() => unwrap<AgentStats>(api.agent.get('/agent/statistics')), 30000);
  const ai = usePolling(() => unwrap<AiUsage>(api.ai.get('/chat/usage')), 60000);

  if (summary.loading && !summary.data) return <Spinner />;
  if (summary.error && !summary.data) {
    return (
      <>
        <PageHeader title={t.dashboard.title} />
        <ErrorPanel
          error={summary.error}
          onRetry={summary.reload}
          title={t.agent.unreachable}
        />
      </>
    );
  }

  const s = summary.data;
  const channelData = s
    ? [
        { name: t.dashboard.onlineSales, value: s.channels.online },
        { name: t.dashboard.branchSales, value: s.channels.branch },
        { name: 'Mobile', value: s.channels.mobile },
        { name: t.nav.agent, value: s.channels.agentSimulation },
      ].filter((entry) => entry.value > 0)
    : [];

  return (
    <>
      <PageHeader
        title={t.dashboard.title}
        subtitle={s ? fill(t.dashboard.subtitle, { from: date(s.range.from), to: date(s.range.to) }) : undefined}
        actions={
          <button type="button" onClick={summary.reload} className="btn-secondary">
            {t.common.refresh}
          </button>
        }
      />

      {/* ---- headline KPIs (spec §11.5) ---- */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t.dashboard.totalSales} value={s ? money(s.sales.totalSales) : null}
             hint={fill(t.dashboard.invoicesCount, { count: n(s?.sales.invoiceCount ?? 0) })} />
        <Kpi label={t.dashboard.averageOrderValue} value={s ? money(s.sales.averageOrderValue) : null} />
        <Kpi
          label={t.dashboard.paymentSuccess}
          value={s ? `${n(s.payments.successRate)}%` : null}
          hint={fill(t.dashboard.ofAttempts, {
            succeeded: n(s?.payments.succeeded ?? 0), attempted: n(s?.payments.attempted ?? 0),
          })}
          tone={!s ? 'default' : s.payments.successRate >= 90 ? 'good' : s.payments.successRate >= 75 ? 'warn' : 'bad'}
        />
        <Kpi
          label={t.dashboard.returnRate}
          value={s ? `${n(s.returns.returnRate)}%` : null}
          hint={s ? fill(t.dashboard.refunded, {
            count: n(s.returns.count), amount: money(s.returns.refundedAmount),
          }) : undefined}
          tone={!s ? 'default' : s.returns.returnRate <= 10 ? 'good' : s.returns.returnRate <= 20 ? 'warn' : 'bad'}
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t.dashboard.onlineSales} value={s ? money(s.channels.online) : null} />
        <Kpi label={t.dashboard.branchSales} value={s ? money(s.channels.branch) : null} />
        <Kpi label={t.dashboard.activeCustomers} value={s ? n(s.customers.active) : null}
             hint={fill(t.dashboard.newCustomers, { count: n(s?.customers.new ?? 0) })} />
        <Kpi
          label={t.dashboard.agentOrdersHour}
          value={agent.data ? n(agent.data.throughput.ordersInLastHour) : agent.error ? null : '—'}
          hint={agent.data?.lastRun
            ? fill(t.dashboard.lastRun, { status: agent.data.lastRun.status.toLowerCase() })
            : undefined}
        />
      </div>

      {/* ---- charts ---- */}
      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <Card title={t.dashboard.dailySales} className="lg:col-span-2">
          {series.data?.series.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series.data.series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#355872" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#355872" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E6EB" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9097AC' }} tickLine={false} axisLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9097AC' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value: number) => (value >= 1000 ? `${Math.round(value / 1000)}k` : String(value))}
                />
                <Tooltip
                  formatter={(value: number) => [money(value), t.analytics.sales]}
                  contentStyle={{ borderRadius: 8, border: '1px solid #D0D3DC', fontSize: 12 }}
                />
                <Area type="monotone" dataKey="total" stroke="#355872" strokeWidth={2} fill="url(#salesFill)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-12 text-center text-sm text-mist-500">{t.dashboard.noSales}</p>
          )}
        </Card>

        <Card title={t.dashboard.channelMix}>
          {channelData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={channelData} dataKey="value" nameKey="name" innerRadius={54} outerRadius={86} paddingAngle={2}>
                  {channelData.map((_, index) => (
                    <Cell key={index} fill={CHANNEL_COLOURS[index % CHANNEL_COLOURS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => money(value)}
                  contentStyle={{ borderRadius: 8, border: '1px solid #D0D3DC', fontSize: 12 }}
                />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-12 text-center text-sm text-mist-500">{t.dashboard.noRevenue}</p>
          )}
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card title={t.dashboard.salesByBranch}>
          {branches.data?.branches.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={branches.data.branches.slice(0, 8)}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E6EB" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#9097AC' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                />
                <YAxis type="category" dataKey="code" width={62} tick={{ fontSize: 11, fill: '#555C72' }} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(value: number) => [money(value), t.analytics.sales]}
                  labelFormatter={(code: string) =>
                    branches.data?.branches.find((b) => b.code === code)?.name ?? code
                  }
                  contentStyle={{ borderRadius: 8, border: '1px solid #D0D3DC', fontSize: 12 }}
                />
                <Bar dataKey="totalSales" fill="#355872" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-12 text-center text-sm text-mist-500">{t.dashboard.noBranchSales}</p>
          )}
        </Card>

        <Card title={t.dashboard.bestProducts}>
          <Table head={[t.dashboard.product, t.dashboard.units, t.dashboard.revenue]} empty={!products.data?.products.length}>
            {products.data?.products.map((product) => (
              <tr key={product.productId} className="hover:bg-mist-50">
                <Td>
                  <p className="font-medium text-mist-800">{field(product, 'productName')}</p>
                  <p className="ltr-embed font-mono text-xs text-mist-500">{product.sku}</p>
                </Td>
                <Td className="tabular text-end text-mist-600">{n(product.unitsSold)}</Td>
                <Td className="tabular text-end font-medium text-deep-600">{money(product.revenue)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>

      {/* ---- operational attention ---- */}
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card
          title={t.dashboard.lowStock}
          action={
            inventory.data?.lowStock.length ? (
              <Badge tone="warn">
                <AlertTriangle size={11} className="me-1" aria-hidden />
                {fill(t.dashboard.lines, { count: n(inventory.data.lowStock.length) })}
              </Badge>
            ) : undefined
          }
        >
          <Table head={[t.dashboard.product, t.dashboard.warehouse, t.dashboard.available, t.dashboard.reorderAt]} empty={!inventory.data?.lowStock.length}>
            {inventory.data?.lowStock.slice(0, 8).map((row, index) => (
              <tr key={index} className="hover:bg-mist-50">
                <Td>
                  <p className="font-medium text-mist-800">{field(row, 'name')}</p>
                  <p className="ltr-embed font-mono text-xs text-mist-500">{row.sku}</p>
                </Td>
                <Td className="text-mist-600">{field(row, 'warehouseName')}</Td>
                <Td className="tabular text-end font-semibold text-warning-fg">{row.quantityAvailable}</Td>
                <Td className="tabular text-end text-mist-500">{row.reorderLevel}</Td>
              </tr>
            ))}
          </Table>
        </Card>

        <Card title={t.dashboard.platformActivity}>
          <dl className="space-y-3 text-sm">
            <Row label={t.dashboard.runsCompleted} value={agent.data ? n(agent.data.runs.completed) : '—'} />
            <Row label={t.dashboard.runsFailed} value={agent.data ? n(agent.data.runs.failed) : '—'}
                 tone={agent.data?.runs.failed ? 'bad' : 'default'} />
            <Row label={t.dashboard.runsLastHour} value={agent.data ? n(agent.data.throughput.runsInLastHour) : '—'} />
            <Row label={t.dashboard.aiAnswers} value={ai.data ? n(ai.data.answers) : '—'} />
            <Row label={t.dashboard.aiLatency} value={ai.data ? `${n(ai.data.averageLatencyMs)} ms` : '—'} />
            <Row
              label={t.dashboard.aiVerified}
              value={ai.data ? `${n(ai.data.verifiedShare)}%` : '—'}
              tone={ai.data && ai.data.verifiedShare >= 90 ? 'good' : 'default'}
            />
          </dl>

          {ai.data && !ai.data.modelConfigured && (
            <p className="mt-4 rounded-md border border-warning-border bg-warning-bg p-3 text-xs text-warning-fg">
              {t.dashboard.noModelWarning}
            </p>
          )}

          <p className="mt-4 flex items-center gap-1.5 text-xs text-mist-500">
            <TrendingUp size={12} aria-hidden />
            {t.dashboard.autoRefresh}
          </p>
        </Card>
      </div>
    </>
  );
}

function Row({
  label, value, tone = 'default',
}: { label: string; value: string; tone?: 'default' | 'good' | 'bad' }) {
  const colour = tone === 'good' ? 'text-success-fg' : tone === 'bad' ? 'text-danger-fg' : 'text-mist-800';
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-mist-100 pb-2 last:border-0">
      <dt className="text-mist-600">{label}</dt>
      <dd className={`tabular font-medium ${colour}`}>{value}</dd>
    </div>
  );
}
